import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  CircularProgress, Alert, TextField, InputAdornment, Avatar,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Tabs, Tab,
  Autocomplete, ImageList, ImageListItem, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ConstructionIcon from '@mui/icons-material/Construction';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import CostControlTab from '../components/CostControlTab';
import {
  getMaquinas, crearMaquina, actualizarMaquina, getMaquinaDetalle,
  subirDocumentoMaquina, getClientes, getMisPermisos, getMaquinariaStock,
  getTelemetriaMaquina, sincronizarMachineLink,
  getDocumentoMaquinaBlob, borrarDocumentoMaquina, exportarMaquinasExcel
} from '../api';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_API_URL;

interface Maquina {
  id: number;
  modelo: string;
  numero_serie: string;
  fabricante: string;
  estado: string;
  fin_garantia: string;
  horas_trabajo: number;
  cliente_nombre: string;
  ubicacion: string;
}

const estadoColor: any = {
  operativa: 'success',
  averiada: 'error',
  en_mantenimiento: 'warning',
  baja: 'default'
};
const DIVISIONES = ['SANY Cranes', 'SANY Robotics', 'SANY Marine', 'TII SCHEUERLE'];
const TABS_DIVISION = ['Todas', ...DIVISIONES, 'Sin asignar'];

const FICHA_VACIA = {
  modelo: '', numero_serie: '', numero_interno: '', fabricante: '',
  año_fabricacion: 2024, cliente_id: null as any, ubicacion: '',
  estado: 'operativa', fecha_compra: '', fin_garantia: '',
    proximo_mantenimiento: '', contrato_mantenimiento: 0, horas_trabajo: 0, notas: '',
  division: ''
};

export default function Maquinas() {
  const [maquinas, setMaquinas] = useState<Maquina[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [clientesLista, setClientesLista] = useState<any[]>([]);
  const [permisos, setPermisos] = useState<string[]>([]);
  const [tab, setTab] = useState(0);
  const [maquinariaStock, setMaquinariaStock] = useState<any[]>([]);
  const [sincronizando, setSincronizando] = useState(false);
  const [vista, setVista] = useState<'tarjetas' | 'lista'>(
    (localStorage.getItem('hei_vista_maquinas') as any) || 'tarjetas'
  );
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(
    parseInt(localStorage.getItem('hei_filas_maquinas') || '25')
  );

  const cambiarVista = (v: 'tarjetas' | 'lista' | null) => {
    if (!v) return;
    setVista(v);
    localStorage.setItem('hei_vista_maquinas', v);
  };

  // Dialog crear/editar
  const [ficha, setFicha] = useState<any>(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const esAdmin = user.rol === 'admin';
  const puedeGestionar = esAdmin || permisos.includes('gestionar_maquinas');
  const puedeVerCC = esAdmin || permisos.includes('cost_control_maquinas');

  const cargar = () => {
    setLoading(true);
    getMaquinas()
      .then(data => setMaquinas(data.filter((m: Maquina) =>
        !search || m.modelo?.toLowerCase().includes(search.toLowerCase()) ||
        m.numero_serie?.toLowerCase().includes(search.toLowerCase()) ||
        m.cliente_nombre?.toLowerCase().includes(search.toLowerCase())
      )))
      .catch(() => setError('Error cargando máquinas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [search]);
  useEffect(() => {
    getClientes().then(setClientesLista);
    getMaquinariaStock().then(setMaquinariaStock).catch(() => setMaquinariaStock([]));
    if (!esAdmin) getMisPermisos().then(setPermisos).catch(() => setPermisos([]));
  }, []);

  // Máquinas visibles según la pestaña de división
  const maquinasVisibles = maquinas.filter((m: any) => {
    if (tab === 0) return true;
    if (tab === TABS_DIVISION.length - 1) return !m.division;
    return m.division === TABS_DIVISION[tab];
  });
  // Vuelve a la primera página al cambiar de división o de búsqueda
  useEffect(() => { setPagina(0); }, [tab, search]);

  const maquinasPagina = maquinasVisibles.slice(
    pagina * filasPorPagina, pagina * filasPorPagina + filasPorPagina
  );

  const garantiaOk = (fecha: string) => fecha && fecha > new Date().toISOString().split('T')[0];

  const nuevaMaquina = () => setFicha({ ...FICHA_VACIA });
    const exportarExcel = async () => {
    const d: any = await exportarMaquinasExcel(0);
    const filas = (d.items || []).map((m: any) => {
      const coste = (m.coste_horas||0) + (m.coste_material||0) + (m.coste_analitico||0);
      return {
        'Nº serie': m.numero_serie || '',
        'Modelo': (m.modelo || '').replace('SANY ', ''),
        'División': m.division || '',
        'Cliente': m.cliente || '',
        'Nº averías': m.n_averias || 0,
        'Mecánicas': m.av_mecanico || 0,
        'Eléctricas': m.av_electrico || 0,
        'Hidráulicas': m.av_hidraulico || 0,
        'Neumáticas': m.av_neumatico || 0,
        'En chasis': m.av_chasis || 0,
        'En superestructura': m.av_superestructura || 0,
        'Primera avería': (m.primera_averia || '').slice(0, 10),
        'Última avería': (m.ultima_averia || '').slice(0, 10),
        'Coste horas €': m.coste_horas || 0,
        'Coste material €': m.coste_material || 0,
        'Coste analítico €': m.coste_analitico || 0,
        'Coste total €': Math.round(coste * 100) / 100,
        'Ingresos €': m.ingreso_analitico || 0,
        'Margen €': Math.round(((m.ingreso_analitico||0) - coste) * 100) / 100,
      };
    });
    if (!filas.length) { alert('No hay máquinas que exportar'); return; }
    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [{wch:20},{wch:16},{wch:14},{wch:28},{wch:11},{wch:11},{wch:11},{wch:12},{wch:11},{wch:10},{wch:18},{wch:14},{wch:14},{wch:13},{wch:15},{wch:16},{wch:13},{wch:11},{wch:11}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Máquinas');
    XLSX.writeFile(wb, `Maquinas_HEI_${new Date().toISOString().slice(0,10)}.xlsx`);
  };
  const handleSincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await sincronizarMachineLink();
      alert(`✅ Sincronización completada:\n${r.creadas} grúas nuevas añadidas\n${r.vinculadas} vinculadas\n${r.total_machinelink} en MachineLink`);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSincronizando(false);
    }
  };
  const editarMaquina = (m: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setFicha({ ...FICHA_VACIA, ...m, cliente_id: m.cliente_id || null });
  };

  const guardar = async () => {
    if (!ficha.modelo || !ficha.numero_serie) {
      alert('Modelo y número de serie son obligatorios');
      return;
    }
    try {
      if (ficha.id) await actualizarMaquina(ficha.id, ficha);
      else await crearMaquina(ficha);
      setFicha(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar la máquina');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          🏗 Máquinas
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="outlined" startIcon={<span>📊</span>} onClick={exportarExcel}>
            Exportar a Excel
          </Button>
          {puedeGestionar && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={nuevaMaquina}
              sx={{ bgcolor: '#4f46e5' }}>
              Nueva máquina
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {puedeGestionar && (
          <Button
            variant="contained"
            onClick={handleSincronizar}
            disabled={sincronizando}
            startIcon={sincronizando ? <CircularProgress size={18} color="inherit" /> : <span>🛰</span>}
            sx={{ bgcolor: '#4f46e5' }}
          >
            {sincronizando ? 'Sincronizando...' : 'Sincronizar con MachineLink'}
          </Button>
        )}
        <Button
          variant="outlined" startIcon={<OpenInNewIcon />}
          onClick={() => window.open('https://sis-global.sanygroup.com/login?redirect=%2Findex', '_blank')}
          sx={{ borderColor: '#4f46e5', color: '#4f46e5' }}
        >
          SANY SIS
        </Button>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable" scrollButtons="auto">
        {TABS_DIVISION.map((d, i) => {
          const n = i === 0
            ? maquinas.length
            : i === TABS_DIVISION.length - 1
              ? maquinas.filter((m: any) => !m.division).length
              : maquinas.filter((m: any) => m.division === d).length;
          return <Tab key={d} label={`${d} (${n})`} />;
        })}
      </Tabs>

      {(
        <Box>
      <TextField
        placeholder="Buscar por modelo, serie, cliente..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 3, minWidth: { xs: '100%', sm: 280 } }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
        }}
      />

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {maquinasVisibles.length} máquinas
        </Typography>
        <ToggleButtonGroup size="small" value={vista} exclusive
          onChange={(_, v) => cambiarVista(v)}>
          <ToggleButton value="tarjetas">
            <Tooltip title="Vista de tarjetas"><ViewModuleIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="lista">
            <Tooltip title="Vista de lista"><ViewListIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* VISTA LISTA */}
      {vista === 'lista' && (
        <TableContainer component={Paper} sx={{ borderRadius: 3, mb: 2, maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: { xs: 480, md: 900 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                <TableCell sx={{ color: 'white' }}>Modelo</TableCell>
                <TableCell sx={{ color: 'white' }}>Nº Serie</TableCell>
                <TableCell sx={{ color: 'white', display: { xs: 'none', lg: 'table-cell' } }}>División</TableCell>
                <TableCell sx={{ color: 'white', display: { xs: 'none', md: 'table-cell' } }}>Cliente</TableCell>
                <TableCell sx={{ color: 'white', display: { xs: 'none', lg: 'table-cell' } }}>Ubicación</TableCell>
                <TableCell sx={{ color: 'white', display: { xs: 'none', md: 'table-cell' } }}>Horas</TableCell>
                <TableCell sx={{ color: 'white' }}>Estado</TableCell>
                <TableCell sx={{ color: 'white', display: { xs: 'none', sm: 'table-cell' } }}>Garantía</TableCell>
                <TableCell sx={{ color: 'white', width: 60 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {maquinasPagina.map((m: any) => (
                <TableRow key={m.id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => setDetalleId(m.id)}>
                  <TableCell sx={{ fontWeight: 600 }}>{m.modelo}</TableCell>
                  <TableCell>{m.numero_serie}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="caption" color="text.secondary">
                      {m.division || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{m.cliente_nombre || '—'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{m.ubicacion || '—'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{m.horas_trabajo || 0}h</TableCell>
                  <TableCell>
                    <Chip label={m.estado} color={estadoColor[m.estado] || 'default'} size="small" />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip label={garantiaOk(m.fin_garantia) ? '✅' : '❌'} size="small"
                      color={garantiaOk(m.fin_garantia) ? 'success' : 'error'} />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 60, px: 0.5 }}>
                    {puedeGestionar && (
                      <IconButton size="small" onClick={(e) => editarMaquina(m, e)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {maquinasVisibles.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  No hay máquinas en esta división
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={maquinasVisibles.length}
            page={pagina}
            onPageChange={(_, p) => setPagina(p)}
            rowsPerPage={filasPorPagina}
            onRowsPerPageChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setFilasPorPagina(n);
              localStorage.setItem('hei_filas_maquinas', String(n));
              setPagina(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </TableContainer>
      )}

      <Grid container spacing={2} sx={{ display: vista === 'tarjetas' ? 'flex' : 'none' }}>
        {maquinasVisibles.map((m) => (
          <Grid item xs={12} sm={6} lg={4} key={m.id}>
            <Card
              sx={{ borderRadius: 3, height: 250, cursor: 'pointer',
                display: 'flex', flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 } }}
              onClick={() => setDetalleId(m.id)}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#1a1a2e', width: 40, height: 40 }}>
                      <ConstructionIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{m.modelo}</Typography>
                      <Typography variant="caption" color="text.secondary">#{m.numero_serie}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Chip label={m.estado} color={estadoColor[m.estado] || 'default'} size="small" />
                    {puedeGestionar && (
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={(e) => editarMaquina(m, e)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    👤 {m.cliente_nombre || 'Sin cliente'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    📍 {m.ubicacion || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ⏱ {m.horas_trabajo || 0}h trabajo
                  </Typography>
                  <Chip
                    label={garantiaOk(m.fin_garantia) ? '✅ En garantía' : '❌ Garantía vencida'}
                    color={garantiaOk(m.fin_garantia) ? 'success' : 'error'}
                    size="small"
                    sx={{ width: 'fit-content', mt: 0.5 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
        </Box>
      )}

      {/* Dialog crear/editar máquina */}
      <Dialog open={!!ficha} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setFicha(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{ficha?.id ? '✏️ Editar máquina' : '➕ Nueva máquina'}</DialogTitle>
        <DialogContent>
          {ficha && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Modelo *" value={ficha.modelo} onChange={(e) => setFicha({ ...ficha, modelo: e.target.value })} />
                <TextField fullWidth label="Nº Serie *" value={ficha.numero_serie} onChange={(e) => setFicha({ ...ficha, numero_serie: e.target.value })} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Nº Interno HEI" value={ficha.numero_interno || ''} onChange={(e) => setFicha({ ...ficha, numero_interno: e.target.value })} />
                <TextField fullWidth label="Fabricante" value={ficha.fabricante || ''} onChange={(e) => setFicha({ ...ficha, fabricante: e.target.value })} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Año fabricación" type="number" value={ficha.año_fabricacion || ''} onChange={(e) => setFicha({ ...ficha, año_fabricacion: parseInt(e.target.value) || null })} />
                <FormControl fullWidth>
                  <InputLabel>Contrato mant.</InputLabel>
                  <Select value={ficha.contrato_mantenimiento || 0} label="Contrato mant." onChange={(e) => setFicha({ ...ficha, contrato_mantenimiento: e.target.value })}>
                    <MenuItem value={0}>No</MenuItem>
                    <MenuItem value={1}>Sí</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Autocomplete
                options={clientesLista}
                getOptionLabel={(c: any) => c.nombre || ''}
                value={clientesLista.find((c: any) => c.id === ficha.cliente_id) || null}
                onChange={(_, v: any) => setFicha({ ...ficha, cliente_id: v ? v.id : null })}
                renderInput={(p) => <TextField {...p} label="Cliente propietario" />}
              />
              <FormControl fullWidth>
                <InputLabel>División</InputLabel>
                <Select value={ficha.division || ''} label="División"
                  onChange={(e) => setFicha({ ...ficha, division: e.target.value })}>
                  <MenuItem value="">— Sin asignar —</MenuItem>
                  {DIVISIONES.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Ubicación" value={ficha.ubicacion || ''} onChange={(e) => setFicha({ ...ficha, ubicacion: e.target.value })} />
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select value={ficha.estado} label="Estado" onChange={(e) => setFicha({ ...ficha, estado: e.target.value })}>
                    <MenuItem value="operativa">Operativa</MenuItem>
                    <MenuItem value="averiada">Averiada</MenuItem>
                    <MenuItem value="en_mantenimiento">En mantenimiento</MenuItem>
                    <MenuItem value="baja">Baja</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Fecha de entrega" type="date" InputLabelProps={{ shrink: true }} value={ficha.fecha_compra || ''} onChange={(e) => setFicha({ ...ficha, fecha_compra: e.target.value })} />
                <TextField fullWidth label="Fin de garantía" type="date" InputLabelProps={{ shrink: true }} value={ficha.fin_garantia || ''} onChange={(e) => setFicha({ ...ficha, fin_garantia: e.target.value })} />
              </Box>
              <TextField fullWidth label="Notas" multiline rows={2} value={ficha.notas || ''} onChange={(e) => setFicha({ ...ficha, notas: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFicha(null)}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" sx={{ bgcolor: '#4f46e5' }}>
            {ficha?.id ? 'Guardar cambios' : 'Crear máquina'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog detalle */}
      {detalleId && (
            <DetalleMaquinaDialog maquinaId={detalleId} puedeGestionar={puedeGestionar} puedeVerCC={puedeVerCC} onClose={() => setDetalleId(null)} onActualizado={cargar} />
      )}
    </Box>
  );
}

function DetalleMaquinaDialog({ maquinaId, puedeGestionar, puedeVerCC, onClose, onActualizado }: any) {
  const [maquina, setMaquina] = useState<any>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [docDialog, setDocDialog] = useState(false);
  const [docDatos, setDocDatos] = useState({ titulo: '', tipo: 'Manual técnico' });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [fotoDialog, setFotoDialog] = useState(false);
  const [fotoTitulo, setFotoTitulo] = useState('');
  const [fotoFile, setFotoFile] = useState<File | null>(null);

  const cargar = () => {
    getMaquinaDetalle(maquinaId).then(setMaquina).finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [maquinaId]);

  const handleSubirDoc = async () => {
    if (!docFile || !docDatos.titulo) return;
    await subirDocumentoMaquina(maquinaId, docDatos.titulo, docDatos.tipo, docFile);
    setDocDialog(false);
    setDocDatos({ titulo: '', tipo: 'Manual técnico' });
    setDocFile(null);
    cargar();
  };

  const verDocumento = async (docId: number) => {
    try {
      const blob = await getDocumentoMaquinaBlob(docId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      alert('No se pudo abrir: ' + (e.response?.data?.detail || e.message));
    }
  };

  const eliminarDocumento = async (doc: any) => {
    if (!window.confirm(`¿Borrar "${doc.titulo}"? No se puede deshacer.`)) return;
    try {
      await borrarDocumentoMaquina(doc.id);
      cargar();
    } catch (e: any) {
      alert('No se pudo borrar: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSubirFoto = async () => {
    if (!fotoFile) return;
    await subirDocumentoMaquina(maquinaId, fotoTitulo || 'Foto', 'Foto', fotoFile);
    setFotoDialog(false);
    setFotoTitulo('');
    setFotoFile(null);
    cargar();
  };

  if (loading || !maquina) return (
    <Dialog open onClose={onClose}><DialogContent><CircularProgress /></DialogContent></Dialog>
  );

  const fotos = maquina.documentos?.filter((d: any) => d.tipo === 'Foto') || [];
  const docs = maquina.documentos?.filter((d: any) => d.tipo !== 'Foto') || [];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>🏗 {maquina.modelo} #{maquina.numero_serie}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}><Typography variant="body2">👤 Cliente: <b>{maquina.cliente_nombre || '—'}</b></Typography></Grid>
          <Grid item xs={6}><Typography variant="body2">📍 Ubicación: <b>{maquina.ubicacion || '—'}</b></Typography></Grid>
          <Grid item xs={6}><Typography variant="body2">⏱ Horas: <b>{maquina.horas_trabajo || 0}h</b></Typography></Grid>
          <Grid item xs={6}><Typography variant="body2">🛡 Garantía hasta: <b>{maquina.fin_garantia || '—'}</b></Typography></Grid>
        </Grid>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
          <Tab label="🛰 Telemetría" />
          <Tab label="🔧 Intervenciones" />
          <Tab label="📄 Documentos" />
          <Tab label="📷 Galería" />
          {puedeVerCC && <Tab label="💰 Cost control" />}
        </Tabs>

        {tab === 0 && <TelemetriaMaquina maquinaId={maquina.id} />}
        {tab === 4 && puedeVerCC && <CostControlTab maquinaId={maquina.id} />}

        {tab === 1 && (
          <Box>
            {maquina.intervenciones?.length === 0 && <Alert severity="info">No hay intervenciones registradas.</Alert>}
            {maquina.intervenciones?.map((i: any) => (
              <Card key={i.id} sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 600 }}>#{i.id} — {i.tipo_averia || i.tipo_intervencion}</Typography>
                    <Chip label={i.estado} size="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {i.fecha?.slice(0, 10)} — {i.tecnico_nombre} — {i.horas}h
                  </Typography>
                  {i.informe_texto && (
                    <Typography variant="body2" sx={{ mt: 1 }}>{i.informe_texto.slice(0, 150)}...</Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {tab === 2 && (
          <Box>
            {puedeGestionar && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDocDialog(true)} sx={{ mb: 2 }}>
                Subir documento
              </Button>
            )}
            {docs.length === 0 && <Alert severity="info">No hay documentos.</Alert>}
            {docs.map((d: any) => (
              <Card key={d.id} sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PictureAsPdfIcon color="error" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.titulo}</Typography>
                    <Typography variant="caption" color="text.secondary">{d.tipo} — {d.fecha?.slice(0, 10)}</Typography>
                  </Box>
                  <Button size="small" onClick={() => verDocumento(d.id)}>Ver</Button>
                  {puedeGestionar && (
                    <IconButton size="small" color="error" title="Borrar documento"
                      onClick={() => eliminarDocumento(d)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {tab === 3 && (
          <Box>
            {puedeGestionar && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setFotoDialog(true)} sx={{ mb: 2 }}>
                Subir foto
              </Button>
            )}
            {fotos.length === 0 && <Alert severity="info">No hay fotos.</Alert>}
            <ImageList cols={3} gap={8}>
              {fotos.map((f: any) => (
                <ImageListItem key={f.id}>
                  <img src={`${API}/documentos_maquina/${f.id}/archivo`} alt={f.titulo} loading="lazy" style={{ borderRadius: 8 }} />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      {/* Dialog subir documento */}
      <Dialog open={docDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setDocDialog(false); } }} maxWidth="sm" fullWidth>
        <DialogTitle>📄 Subir documento</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Título" value={docDatos.titulo} onChange={(e) => setDocDatos({ ...docDatos, titulo: e.target.value })} />
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={docDatos.tipo} label="Tipo" onChange={(e) => setDocDatos({ ...docDatos, tipo: e.target.value })}>
                <MenuItem value="Manual técnico">Manual técnico</MenuItem>
                <MenuItem value="Manual de servicio">Manual de servicio</MenuItem>
                <MenuItem value="Tablas de carga">Tablas de carga</MenuItem>
                <MenuItem value="Certificado inspección">Certificado inspección</MenuItem>
                <MenuItem value="Otro">Otro</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" component="label">
              {docFile ? docFile.name : 'Seleccionar archivo PDF'}
              <input type="file" accept=".pdf" hidden onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubirDoc} variant="contained" sx={{ bgcolor: '#4f46e5' }}>Subir</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog subir foto */}
      <Dialog open={fotoDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setFotoDialog(false); } }} maxWidth="sm" fullWidth>
        <DialogTitle>📷 Subir foto</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Título (opcional)" value={fotoTitulo} onChange={(e) => setFotoTitulo(e.target.value)} />
            <Button variant="outlined" component="label">
              {fotoFile ? fotoFile.name : 'Seleccionar foto'}
              <input type="file" accept="image/*" hidden onChange={(e) => setFotoFile(e.target.files?.[0] || null)} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFotoDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubirFoto} variant="contained" sx={{ bgcolor: '#4f46e5' }}>Subir</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
// ════════════════════════════════════════════════════════════
// COMPONENTE DE TELEMETRÍA — insertar en Maquinas.tsx
// Muestra los datos en tiempo real de MachineLink + mapa de ubicación.
// ════════════════════════════════════════════════════════════

function TelemetriaMaquina({ maquinaId }: { maquinaId: number }) {
  const [tele, setTele] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = () => {
    setLoading(true);
    setError('');
    getTelemetriaMaquina(maquinaId)
      .then(setTele)
      .catch(() => setError('No se pudo obtener la telemetría'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [maquinaId]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
  );

  if (error) return <Alert severity="warning">{error}</Alert>;

  if (!tele?.vinculada) {
    return <Alert severity="info">Esta máquina no está vinculada a MachineLink, no hay telemetría disponible.</Alert>;
  }

  if (tele.error) {
    return <Alert severity="warning">MachineLink no respondió: {tele.error}</Alert>;
  }

  const d = tele.datos || {};

  // Tarjetas de datos (solo las que tienen valor)
  const metricas = [
    { label: 'Horas motor', valor: d.horas_motor, unidad: 'h', icono: '⏱' },
    { label: 'Horas grúa', valor: d.horas_grua, unidad: 'h', icono: '🏗' },
    { label: 'Kilómetros', valor: d.kilometros, unidad: 'km', icono: '🛣' },
    { label: 'Combustible', valor: d.combustible, unidad: '%', icono: '⛽' },
    { label: 'RPM motor', valor: d.rpm_motor, unidad: 'rpm', icono: '⚙️' },
    { label: 'Velocidad', valor: d.velocidad, unidad: 'km/h', icono: '💨' },
  ].filter(m => m.valor !== null && m.valor !== undefined);

  const tieneGPS = d.latitud && d.longitud;
  const fechaAct = d.ultima_actualizacion
    ? new Date(d.ultima_actualizacion).toLocaleString('es-ES')
    : null;

  // Bounding box para el iframe de OpenStreetMap
  const bbox = tieneGPS
    ? `${d.longitud - 0.01},${d.latitud - 0.01},${d.longitud + 0.01},${d.latitud + 0.01}`
    : '';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label="🛰 En vivo · MachineLink" color="success" size="small" />
          {fechaAct && (
            <Typography variant="caption" color="text.secondary">
              Última conexión: {fechaAct}
            </Typography>
          )}
        </Box>
        <Button size="small" onClick={cargar}>🔄 Actualizar</Button>
      </Box>

      {/* Tarjetas de métricas */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {metricas.map((m) => (
          <Grid item xs={6} sm={4} key={m.label}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {m.icono} {m.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {typeof m.valor === 'number' ? m.valor.toLocaleString('es-ES') : m.valor}
                  <Typography component="span" variant="caption" color="text.secondary"> {m.unidad}</Typography>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Mapa de ubicación */}
      {tieneGPS ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>📍 Ubicación actual</Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <iframe
              title="Ubicación de la máquina"
              width="100%" height="280" style={{ border: 0, display: 'block' }}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${d.latitud},${d.longitud}`}
            />
          </Box>
          <Button
            size="small" sx={{ mt: 1 }}
            href={`https://www.google.com/maps?q=${d.latitud},${d.longitud}`}
            target="_blank"
          >
            Abrir en Google Maps ↗
          </Button>
        </Box>
      ) : (
        <Alert severity="info">Esta máquina no reporta ubicación GPS.</Alert>
      )}
    </Box>
  );
}